import { useState, useEffect } from "react";
import { eventModelApi } from "@/services/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { CalendarDays, Plus, X, Loader2, Tag } from "lucide-react";

export default function EventModelSetup() {
  const [tab, setTab] = useState("types");
  const [eventTypes, setEventTypes] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceDefinitions, setServiceDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);

  const load = () => {
    Promise.all([eventModelApi.listEventTypes(), eventModelApi.listServiceCategories(), eventModelApi.listServiceDefinitions()])
      .then(([et, sc, sd]) => { setEventTypes(et.data); setServiceCategories(sc.data); setServiceDefinitions(sd.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const TABS = [
    { id: "types", label: "Event Types", count: eventTypes.length },
    { id: "categories", label: "Service Categories", count: serviceCategories.length },
    { id: "definitions", label: "Service Definitions", count: serviceDefinitions.length },
  ];

  if (loading) return <div className="p-6 animate-pulse space-y-4"><div className="h-8 bg-[#F3F3F3] rounded-lg w-64"/><div className="h-40 bg-[#F3F3F3] rounded-xl"/></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#181818] font-sans">Event Model Foundation</h1>
        <p className="text-sm text-[#706E6B] mt-1">Configure event types, service categories and service definitions</p>
      </div>

      <div className="flex gap-1 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
              tab === t.id ? "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30" : "text-[#706E6B] hover:text-[#181818]"
            }`}>
            {t.label}
            <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${tab === t.id ? "bg-[#EEF4FF] text-[#0176D3]" : "bg-[#E5E5E5] text-[#706E6B]"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {tab === "types" && (
        <ListSection title="Event Types" onAdd={() => setShowForm("et")} items={eventTypes}
          renderItem={et => <ModelRow key={et.id} title={et.name} code={et.code} desc={et.description} status={et.status} />}
          empty="No event types configured" />
      )}
      {tab === "categories" && (
        <ListSection title="Service Categories" onAdd={() => setShowForm("sc")} items={serviceCategories}
          renderItem={sc => <ModelRow key={sc.id} title={sc.name} code={sc.code} desc={sc.description} status={sc.status} />}
          empty="No service categories configured" />
      )}
      {tab === "definitions" && (
        <ListSection title="Service Definitions" onAdd={() => setShowForm("sd")} items={serviceDefinitions}
          renderItem={sd => (
            <div key={sd.id} className="flex items-center justify-between p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl hover:border-[#0176D3]/30 transition-colors">
              <div>
                <p className="text-sm font-semibold text-[#181818]">{sd.name}</p>
                <p className="text-xs font-mono text-[#706E6B] mt-0.5">{sd.code}</p>
                {sd.category_name && <span className="text-[10px] font-mono text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-1.5 py-0.5 rounded mt-1 inline-block">{sd.category_name}</span>}
              </div>
              <StatusBadge status={sd.status} />
            </div>
          )}
          empty="No service definitions configured" />
      )}

      {showForm === "et" && (
        <Modal title="Add Event Type" onClose={() => setShowForm(null)}>
          <QuickForm fields={[["code","Code","ET-WEDDING",true],["name","Name","Wedding",true],["description","Description","Full wedding ceremony"]]}
            onSubmit={async (d) => { await eventModelApi.createEventType(d); toast.success("Event Type created"); setShowForm(null); load(); }} />
        </Modal>
      )}
      {showForm === "sc" && (
        <Modal title="Add Service Category" onClose={() => setShowForm(null)}>
          <QuickForm fields={[["code","Code","SC-VENUE",true],["name","Name","Venue",true],["description","Description","Venue services"]]}
            onSubmit={async (d) => { await eventModelApi.createServiceCategory(d); toast.success("Service Category created"); setShowForm(null); load(); }} />
        </Modal>
      )}
      {showForm === "sd" && (
        <Modal title="Add Service Definition" onClose={() => setShowForm(null)}>
          <ServiceDefinitionForm categories={serviceCategories} onSuccess={() => { setShowForm(null); load(); }} />
        </Modal>
      )}
    </div>
  );
}

function ServiceDefinitionForm({ categories, onSuccess }) {
  const [form, setForm] = useState({ code: "", name: "", category_id: "", description: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await eventModelApi.createServiceDefinition(form); toast.success("Service Definition created"); onSuccess(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <FormField label="Code" value={form.code} onChange={v=>setForm({...form,code:v})} placeholder="SD-BANQUET" required />
      <FormField label="Name" value={form.name} onChange={v=>setForm({...form,name:v})} placeholder="Banquet Hall" required />
      <SelectField label="Service Category *" value={form.category_id} onChange={v=>setForm({...form,category_id:v})}
        options={[["","Select category"],...categories.map(c=>[c.id,c.name])]} />
      <FormField label="Description" value={form.description} onChange={v=>setForm({...form,description:v})} placeholder="Description" />
      <SubmitBtn loading={loading} label="Create Service Definition" />
    </form>
  );
}

function ListSection({ title, onAdd, items, renderItem, empty }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[#444444] font-sans">{title}</h2>
        <button onClick={onAdd} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] text-xs font-medium hover:bg-[#E5E5E5]">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      <div className="space-y-2">
        {items.map(renderItem)}
        {items.length === 0 && <div className="text-center py-8 text-sm text-[#706E6B] bg-[#F3F3F3] border border-dashed border-[#E5E5E5] rounded-xl">{empty}</div>}
      </div>
    </div>
  );
}
function ModelRow({ title, code, desc, status }) {
  return (
    <div className="flex items-center justify-between p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl hover:border-[#0176D3]/30 transition-colors">
      <div>
        <p className="text-sm font-semibold text-[#181818]">{title}</p>
        <p className="text-xs font-mono text-[#706E6B] mt-0.5">{code}</p>
        {desc && <p className="text-xs text-[#706E6B] mt-0.5">{desc}</p>}
      </div>
      <StatusBadge status={status} />
    </div>
  );
}
function QuickForm({ fields, onSubmit }) {
  const [form, setForm] = useState(Object.fromEntries(fields.map(([k]) => [k, ""])));
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await onSubmit(form); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      {fields.map(([k,l,p,req]) => <FormField key={k} label={l} value={form[k]} onChange={v=>setForm({...form,[k]:v})} placeholder={p} required={req} />)}
      <SubmitBtn loading={loading} label="Create" />
    </form>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-[#F3F3F3] border border-[#E5E5E5] rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-[#181818] font-sans">{title}</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function FormField({ label, value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{label}{required && " *"}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required}
        className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm placeholder:text-[#706E6B] focus:outline-none focus:border-[#0176D3] transition-colors" />
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm focus:outline-none focus:border-[#0176D3] transition-colors">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
function SubmitBtn({ loading, label }) {
  return (
    <button type="submit" disabled={loading}
      className="w-full py-2.5 rounded-lg bg-gradient-to-r from-[#0176D3] to-[#014486] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 mt-2">
      {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : label}
    </button>
  );
}
